import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CelebrationIcon from '@mui/icons-material/Celebration';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  Grid,
  List,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import strings from 'common/Translation/Translate';
import PrimaryButton from 'components/Button/PrimaryButton';
import moment from 'moment';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Config from 'utils/Config';
import axiosInstance from 'utils/axios';
import { futureNoOfDaysList2 } from 'utils/constants/data';
import { identifiers } from 'utils/constants/identifiers';
import { routes } from 'utils/constants/routes';
import { URLS } from 'utils/constants/urls';
import { fetchFromStorage } from 'utils/storage';
import { downloadExportFile, getColor, getLabel } from 'utils/validators/HelperFunctions';
import PreviewContract from '../NewBooking/PreviewContract';
import { CloudDownload } from '@mui/icons-material';
import { formatCurrencyWithSymbol } from 'utils/hooks/getCurrency';
import { getPricePackageName } from 'utils/pricePackage';

interface UpComingBookingProps {
  UpComingBeo: any;
  title: string;
  noOfDays: string;
  setNoOfDays: any;
  maxBodyHeight?: number;
}
const UpComingBeo: FC<UpComingBookingProps> = ({ UpComingBeo, title, noOfDays, setNoOfDays, maxBodyHeight = 280 }) => {
  const futureNoOfDaysOptions = futureNoOfDaysList2(strings);
  const theme = useTheme();
  const navigate = useNavigate();
  const token = fetchFromStorage(identifiers.access_token);
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [previewBEO, setPreviewBEO] = useState('');
  const [fileName, setFileName] = useState<any>('BookingEventOrder');
  const [closeModal, setCloseModal] = useState(false);
  const [selectedId, setSelectedId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const iconsStyle = {
    fontSize: '14px',
    position: 'relative',
    top: 2,
    marginRight: 1,
  };

  const handleChange = (event: SelectChangeEvent) => {
    setNoOfDays(event.target.value);
  };

  const formatINR = (value: number) =>
    formatCurrencyWithSymbol(value);

  const urgencyChip = (startDate: any) => {
    const today = moment().startOf('day');
    const start = moment(startDate).startOf('day');
    const diff = start.diff(today, 'days');
    if (diff < 0) return null;
    if (diff === 0) return { label: 'TODAY', bg: '#FEE2E2', fg: '#991B1B' };
    if (diff === 1) return { label: 'TOMORROW', bg: '#FFEDD5', fg: '#9A3412' };
    return null;
  };

  const downloadBEO = async (id: string, downloadName?: string) => {
    try {
      setIsLoading(true);
      setSelectedId(id);
      axios({
        url: `${Config.BaseURL + URLS.downloadBEOPdf(id)}`,
        method: 'GET',
        data: {},
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      })
        .then(response => {
          setIsLoading(false);
          downloadExportFile(response, 'pdf', downloadName || fileName);
        })
        .catch(err => {
          setIsLoading(false);
        });
    } catch (err: any) {
      setIsLoading(false);
    }
  };

  const handlePreviewBEO = async (id: any) => {
    try {
      setIsLoading(true);
      setSelectedId(id);
      const { status, data } = await axiosInstance.get(URLS.previewBeo(id));
      if (status === 200) {
        setIsLoading(false);
        setCloseModal(true);
        setFileName(data.fileName);
        setPreviewBEO(data.html);
      }
    } catch (error: any) {
      setIsLoading(false);
    }
  };

  const summary = (() => {
    const count = Array.isArray(UpComingBeo) ? UpComingBeo.length : 0;
    const revenue = Array.isArray(UpComingBeo)
      ? UpComingBeo.reduce((acc: number, item: any) => acc + (typeof item?.revenue === 'number' ? item.revenue : 0), 0)
      : 0;
    const pax = Array.isArray(UpComingBeo)
      ? UpComingBeo.reduce(
          (acc: number, item: any) =>
            acc + (typeof (item?.guests ?? item?.pax ?? item?.noOfGuests) === 'number' ? (item?.guests ?? item?.pax ?? item?.noOfGuests) : 0),
          0,
        )
      : 0;

    const noun = count === 1 ? 'Event' : 'Events';
    const parts = [`${count} ${noun}`];
    if (revenue > 0) parts.push(`${formatINR(revenue)} Revenue`);
    if (pax > 0) parts.push(`${pax} pax`);
    return parts.join(' · ');
  })();

  return (
    <>
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Grid container alignItems={'center'} justifyContent={'space-between'} px={0} gap={1}>
            <Grid item xs={12} sm={6} md={8}>
              <Typography fontWeight={600} sx={{ fontSize: '1rem' }}>
                {title}
              </Typography>
            </Grid>
            <Grid item xs={10} sm={5} md={3}>
              <FormControl sx={{ width: '100%' }} size={'small'}>
                <Select value={noOfDays} onChange={handleChange}>
                  {futureNoOfDaysOptions.map((item: any) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: 'text.secondary' }}>
            {summary}
          </Typography>
        </Box>
        <CardContent sx={{ minHeight: '90px', maxHeight: maxBodyHeight, p: 0, pb: 0, overflowY: 'auto', flex: 1 }}>
          {/* <Scrollbar style={{ width: '100%', height: '50vh', fontFamily: theme.typography.fontFamily }}> */}
          <List
            sx={{
              width: '100%',
              bgcolor: 'background.paper',
              pt: 0,
            }}>
            {UpComingBeo?.length > 0 ? (
              UpComingBeo.map((upcoming: any) => (
                <>
                  <Grid container key={upcoming.lineItemId} p={2} rowGap={1}>
                    <Grid item xs={12}>
                      <Box display="flex" flexDirection="column" gap={1} alignItems="flex-start">
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          {upcoming.title && (
                            <Typography variant="subtitle2" sx={{ fontSize: 12.5 }}>
                              {upcoming.title}
                            </Typography>
                          )}
                          {upcoming.corporateBooking && (
                            <Chip
                              label="Corporate"
                              size="small"
                              sx={{
                                backgroundColor: '#E3F2FD',
                                color: '#1976D2',
                                fontSize: 9,
                                fontWeight: 700,
                                height: 18,
                                letterSpacing: 0.35,
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          )}
                          <Chip
                            color={getColor(upcoming)}
                            size="small"
                            label={getLabel(upcoming)}
                            sx={{
                              fontSize: 9,
                              fontWeight: 700,
                              height: 18,
                              letterSpacing: 0.35,
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                          {upcoming.startDate ? (() => {
                            const u = urgencyChip(upcoming.startDate);
                            if (!u) return null;
                            return (
                              <Chip
                                size="small"
                                label={u.label}
                                sx={{
                                  bgcolor: u.bg,
                                  color: u.fg,
                                  fontWeight: 800,
                                  fontSize: 9,
                                  height: 18,
                                  letterSpacing: 0.35,
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            );
                          })() : null}
                          <Typography variant="subtitle2" sx={{ fontSize: 11.5 }}>
                            <CelebrationIcon sx={iconsStyle} /> {upcoming.eventType}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 600, fontSize: 11 }}>
                            · Guests: {upcoming.guests ?? upcoming.pax ?? upcoming.noOfGuests ?? '-'}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="body2" color="text.primary" sx={{ fontSize: 11.5 }}>
                            <CalendarMonthIcon sx={iconsStyle} />
                            {moment(upcoming.startDate).format('DD/MM/YYYY')} -{' '}
                            {moment(upcoming.endDate).format('DD/MM/YYYY')}
                          </Typography>
                          {upcoming.timeSlot && (
                            <Typography variant="body2" color="text.primary" sx={{ fontSize: 11.5 }}>
                              <AccessTimeIcon sx={iconsStyle} />
                              {upcoming.timeSlot}
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 600, fontSize: 11 }}>
                            · Package:{' '}
                            {getPricePackageName(upcoming)} · BEO:{' '}
                            {upcoming.beoStatus ?? upcoming.beo?.status ?? '-'}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 1,
                          mt: 0.5,
                        }}>
                        <PrimaryButton
                          variant="outlined"
                          fullWidth={false}
                          onClick={() => handlePreviewBEO(upcoming.lineItemId)}
                          sx={{
                            minHeight: 'auto',
                            height: '30px',
                            fontSize: '0.72rem',
                            px: 1.25,
                            width: 'auto',
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: 1,
                          }}>
                          Preview
                        </PrimaryButton>
                        <Button
                          variant="outlined"
                          onClick={() => navigate(`${routes.addBeo}/${upcoming.lineItemId}`)}
                          sx={{
                            minHeight: 'auto',
                            height: '30px',
                            fontSize: '0.72rem',
                            px: 1.25,
                            borderRadius: 1,
                            textTransform: 'none',
                            fontWeight: 700,
                          }}>
                          Edit
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<CloudDownload sx={{ fontSize: 16 }} />}
                          onClick={() => downloadBEO(String(upcoming.lineItemId), upcoming.fileName || fileName)}
                          sx={{
                            minHeight: 'auto',
                            height: '30px',
                            fontSize: '0.72rem',
                            px: 1.25,
                            borderRadius: 1,
                            textTransform: 'none',
                            fontWeight: 700,
                          }}>
                          PDF
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                  <Divider component="li" sx={{ my: 1 }} />
                </>
              ))
            ) : (
              <Box sx={{ py: 5, textAlign: 'center' }}>
                <Typography variant="subtitle1" fontWeight={800}>
                  No upcoming BEOs for next week
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  You’re all set.
                </Typography>
              </Box>
            )}
          </List>
          {/* </Scrollbar> */}
        </CardContent>
      </Card>
      {closeModal && (
        <Grid item xs={screenSizeDownSm && 12}>
          <PreviewContract
            headerText={strings.bookings_button_preview_beo}
            downloadButtonText={strings.bookings_button_download_beo}
            downloadContract={() => downloadBEO(String(selectedId), fileName)}
            pdfUrl={previewBEO}
            handleClose={() => {
              setPreviewBEO('');
              setCloseModal(false);
            }}
            openPdfDrawer={closeModal}
            loading={isLoading}
          />
        </Grid>
      )}
    </>
  );
};
export default UpComingBeo;
